import classes from "./InteractiveMap.module.scss"
import { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react"
import { animate, motion, stagger, useAnimate, useMotionValue } from "framer-motion";
import { createUseGesture, pinchAction, dragAction, wheelAction } from "@use-gesture/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { CenterIcon, ZoomInIcon, ZoomOutIcon } from "../../utils/Icons";
import {NAME_SOUND_HOVER, useGameSounds, useMediaQuery } from "@educaplay/core";
import { MAPA_SOUNDS } from "../../utils/constants";
import { Loading } from "@educaplay/core/Icons/Loading";

const useGesture = createUseGesture([pinchAction, dragAction, wheelAction]);

const maxScale = 5;
const minScale = 1;
const RatioWheel = 400;
const ZOOM_TO_POINT_SCALE = 2;
const DELAY_BETWEEN_SOUNDS = 150;
const MAP_ANIMATION_DURATION = 600;

const formatPoint = (point, dimensions) => {
    const x = (point.x / dimensions.width) * dimensions.scaledWidth;
    const y = (point.y / dimensions.height) * dimensions.scaledHeight;

    return {
        ...point,
        x,
        y
    };
}

const InteractiveMap = forwardRef(function InteractiveMap(props, ref) {
    const { src, points, pointOffsetY = 0, enabled = true, animated } = props;
    const [loading, setLoading] = useState(true);
    const [cursor, setCursor] = useState("grab");
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const scale = useMotionValue(1);
    const [zooming, setZooming] = useState(false);
    const [bounds, setBounds] = useState({
        width: 0,
        height: 0,
        x: 0,
        y: 0
    });
    const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 0, height: 0, scaledWidth: 0, scaledHeight: 0 });

    const [pointHoverId, setPointHoverId] = useState(null);

    const imageRef = useRef(null);
    const interactiveRef = useRef(null);
    const isTouchDevice = useMediaQuery("(hover:none)") || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0);
    const isDesktop = useMediaQuery("(min-width: 1024px)"); // revisar
    const gameSound = useGameSounds();


    const formattedPoints = points.map(point => formatPoint(point, originalImageDimensions));
    const [containerRef, anima] = useAnimate();

    useImperativeHandle(ref, () => ({
        moveTo: moveToPoint,
        reset: async () => {
            gameSound.play(MAPA_SOUNDS.MOVE);
            if (scale.get() !== 1) {
                const promises = [
                    animate(scale, 1, {
                        type: "tween",
                        duration: 0.2,
                        ease: [0.25, 1, 0.5, 1],
                    }),
                    centerMap()
                ];

                await Promise.all(promises);
            }
        },
        zoomToPoint: async (point) => {
            await moveToPoint({
                ...point,
                scale: ZOOM_TO_POINT_SCALE
            })
        },
        initialAnimation: async () => {
            const sequence = [
                ["[data-animate-id='image']", { y: 0, opacity: 1 }, { duration: .5 }],
                ["[data-animate-id='point']", { opacity: 1 }, { delay: stagger(0.15), duration: .5 }],
                ["[data-animate-id='nav-button']", { opacity: 1, y: 0, x: 0 }, { delay: stagger(0.15) }]
            ]

            for (let i = 0; i < formattedPoints.length; i++) {
                setTimeout(() => {
                    gameSound.play(MAPA_SOUNDS.PLACE)
                }, i * DELAY_BETWEEN_SOUNDS + MAP_ANIMATION_DURATION);
            }

            return anima(sequence)
        },
        entryMapAnimation: async () => {
            const sequence = [
                ["[data-animate-id='image']", { y: 0, opacity: 1 }, { duration: .5 }],
                ["[data-animate-id='point']", { opacity: 1 }, { delay: stagger(0.15), duration: .5 }],
            ]

            for (let i = 0; i < formattedPoints.length; i++) {
                setTimeout(() => {
                    gameSound.play(MAPA_SOUNDS.PLACE)
                }, i * DELAY_BETWEEN_SOUNDS + MAP_ANIMATION_DURATION);
            }

            return anima(sequence)
        },
        exitMapAnimation: async () => {
            const sequence = [
                ["[data-animate-id='point']", { opacity: 0 }, { delay: stagger(0.15), duration: .5 }],
                ["[data-animate-id='image']", { y: '-100%', opacity: 0 }, { duration: .5 }],
            ]

            return anima(sequence)
        },
        exitPointsAnimation: async () => {
            const sequence = [
                ["[data-animate-id='point']", { opacity: 0 }, { delay: stagger(0.15), duration: .5 }],
            ]

            return anima(sequence)
        },
        entryPointsAnimation: async () => {
            const sequence = [
                ["[data-animate-id='point']", { opacity: 1 }, { delay: stagger(0.15), duration: .5 }],
            ]

            for (let i = 0; i < formattedPoints.length; i++) {
                setTimeout(() => {
                    gameSound.play(MAPA_SOUNDS.PLACE)
                }, i * DELAY_BETWEEN_SOUNDS);
            }

            return anima(sequence)
        }
    }));


    const moveToPoint = async (destination) => {
        if (loading) return;
        const formattedPoint = formatPoint(destination, originalImageDimensions);
        gameSound.play(MAPA_SOUNDS.MOVE);

        const destinationScale = destination.scale ?? scale.get();

        const distanceX = formattedPoint.x;
        const distanceY = formattedPoint.y;

        const center = {
            x: bounds.width * .5,
            y: bounds.height * (((100 + pointOffsetY) * .5) / 100)
        }

        const newX = (center.x - distanceX) * destinationScale + bounds.x;
        const newY = (center.y - distanceY) * destinationScale + bounds.y;


        const animations = [
            animate(scale, destinationScale, {
                type: "tween",
                duration: 0.5,
                ease: [0.25, 1, 0.5, 1],
            }),
            animate(x, newX, {
                type: "tween",
                duration: 0.5,
                ease: [0.25, 1, 0.5, 1],
            }),
            animate(y, newY, {
                type: "tween",
                duration: 0.5,
                ease: [0.25, 1, 0.5, 1],
            }),
        ];

        await Promise.all(animations);

        const newPoint = calculateAdjustedBounds({
            x: newX,
            y: newY,
        });

        const ajustedAnimations = [
            animate(x, newPoint.x, {
                type: "tween",
                duration: 0.3,
                ease: [0.25, 1, 0.5, 1],
            }),
            animate(y, newPoint.y, {
                type: "tween",
                duration: 0.3,
                ease: [0.25, 1, 0.5, 1],
            })
        ];

        await Promise.all(ajustedAnimations);
    }



    const getBounds = useCallback(() => {
        const imageBounds = imageRef.current.getBoundingClientRect();
        const containerBounds = containerRef.current.getBoundingClientRect();

        const heightOverflow = Math.min(imageBounds.height, containerBounds.height) / 2;
        const widthOverflow = Math.min(imageBounds.width, containerBounds.width) / 2;

        const widthOverhang = (imageBounds.width - bounds.width) / 2;
        const heightOverhang = (imageBounds.height - bounds.height) / 2;

        const isWidthOutContainer = imageBounds.width >= containerBounds.width;
        const isHeightOutContainer = imageBounds.height >= containerBounds.height;

        return {
            right: isWidthOutContainer
                ? widthOverhang + widthOverflow
                : containerBounds.width - imageBounds.width + widthOverhang + widthOverflow,
            left: isWidthOutContainer
                ? -(imageBounds.width - containerBounds.width) + widthOverhang - widthOverflow
                : widthOverhang - widthOverflow,
            bottom: isHeightOutContainer
                ? heightOverhang + heightOverflow
                : containerBounds.height - imageBounds.height + heightOverhang + heightOverflow,
            top: isHeightOutContainer
                ? -(imageBounds.height - containerBounds.height) + heightOverhang - heightOverflow
                : heightOverhang - heightOverflow,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bounds]);

    const calculateAdjustedBounds = useCallback((point) => {
        const bounds = getBounds();

        if (point.x < bounds.left) {
            point.x = bounds.left;
        } else if (point.x > bounds.right) {
            point.x = bounds.right;
        }

        if (point.y < bounds.top) {
            point.y = bounds.top;
        } else if (point.y > bounds.bottom) {
            point.y = bounds.bottom;
        }

        return point;
    }, [getBounds]);


    useEffect(() => {
        const handleMouseLeave = () => {
            setCursor("grab");
            setPointHoverId(null);
        }

        document.addEventListener("mouseleave", handleMouseLeave)

        return () => {
            document.removeEventListener("mouseleave", handleMouseLeave);
        }
    }, [])

    useEffect(() => {
        setLoading(true);
        const initilize = () => {
            const containerBounds = containerRef.current.getBoundingClientRect();

            const originalWidth = image.width;
            const originalHeight = image.height;

            const widthRatio = containerBounds.width / originalWidth;
            const heightRatio = containerBounds.height / originalHeight;

            const ratio = Math.min(widthRatio, heightRatio);

            const scaledWidth = originalWidth * ratio;
            const scaledHeight = originalHeight * ratio;

            setOriginalImageDimensions({
                width: originalWidth,
                height: originalHeight,
                scaledWidth,
                scaledHeight
            });

            scale.set(1)
            y.set((containerBounds.height - scaledHeight) / 2);
            x.set((containerBounds.width - scaledWidth) / 2);

            setBounds({
                width: scaledWidth,
                height: scaledHeight,
                x: (containerBounds.width - scaledWidth) / 2,
                y: (containerBounds.height - scaledHeight) / 2
            });
        }

        const image = new Image();
        image.onload = () => {
            initilize();
            setLoading(false);
        };
        image.src = src;

        window.addEventListener("resize", initilize);

        return () => {
            window.removeEventListener("resize", initilize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scale, src, x, y]);

    const centerMap = () => {
        const animations = [
            animate(x, bounds.x, {
                type: "tween",
                duration: 0.15,
                ease: [0.25, 1, 0.5, 1],
            }),
            animate(y, bounds.y, {
                type: "tween",
                duration: 0.15,
                ease: [0.25, 1, 0.5, 1],
            })
        ];

        return Promise.all(animations)
    }

    useGesture({
        onDrag: ({ offset: [dx, dy], pinching, event }) => {
            if (pinching) return;

            setCursor("grabbing");
            x.set(dx);
            y.set(dy);

            event.preventDefault();
            event.stopImmediatePropagation();
        },
        onDragEnd: ({ event }) => {
            setCursor("grab");
            event.preventDefault();
            event.stopPropagation();
        },
        onMouseMove: ({ dragging, event }) => {
            if (dragging) return;

            interactiveRef.current.style.pointerEvents = "none";
            const element = document.elementFromPoint(event.clientX, event.clientY);
            if (element) {
                const isInteractive = element.getAttribute("data-interactive-point");
                if (isInteractive) {
                    if (pointHoverId !== element.parentElement.id) gameSound.play(NAME_SOUND_HOVER);
                    setCursor("pointer");
                    setPointHoverId(element.parentElement.id ?? null);
                } else {
                    setCursor("grab");
                    setPointHoverId(null);
                }
            }
            interactiveRef.current.style.pointerEvents = "all";
        },
        onClick: ({ event, dragging }) => {
            if (dragging) return;

            const mapClickEvent = new Event("map-click", { bubbles: true });
            window.dispatchEvent(mapClickEvent);

            interactiveRef.current.style.pointerEvents = "none";

            const element = document.elementFromPoint(event.clientX, event.clientY);
            if (element) {
                element.click?.();
            }

            interactiveRef.current.style.pointerEvents = "all";
        },
        onPinch: ({ origin: [ox, oy], first, movement: [ms], offset: [s], memo }) => {
            if (first) {
                const bounds = imageRef.current.getBoundingClientRect();
                const tx = ox - (bounds.x + bounds.width / 2)
                const ty = oy - (bounds.y + bounds.height / 2)

                memo = { tx, ty, x: x.get(), y: y.get() }
            }

            const { tx, ty, x: initialX, y: initialY } = memo;

            const newX = initialX - (ms - 1) * tx;
            const newY = initialY - (ms - 1) * ty;

            const point = calculateAdjustedBounds({ x: newX, y: newY });
            y.set(point.y);
            x.set(point.x);
            scale.set(s);

            return memo;
        },
        onWheel: ({ offset: [, oy], movement: [, my], first, memo, event }) => {
            event.preventDefault();
            if (first) {
                const image = imageRef.current.getBoundingClientRect();

                const zoom = scale.get();
                const tx = (event.pageX - (image.x + image.width / 2)) / zoom;
                const ty = (event.pageY - (image.y + image.height / 2)) / zoom;
                const isNotchedWheel = Math.abs(event.wheelDelta ?? 0) > 100;

                const dScale = (-1 * oy) / RatioWheel;

                memo = { tx, ty, initialX: x.get(), initialY: y.get(), isNotchedWheel };

                if (zoom === 1 && dScale === 1) {
                    scale.set(.98)
                    animate(scale, 1, {
                        type: "spring",
                        stiffness: 100,
                        duration: 0.2,
                    });
                    return memo;
                }
            }

            const dScale = (-1 * oy) / RatioWheel;
            const mScale = (-1 * my) / RatioWheel;

            const { tx, ty, initialX, initialY, isNotchedWheel } = memo;
            const dx = initialX - mScale * tx;
            const dy = initialY - mScale * ty;

            if (isNotchedWheel) {
                y.stop();
                x.stop();
                scale.stop();
            }
            const duration = Math.abs(dScale - scale.get()) * 0.5;

            if (dScale === 1) {
                animate(x, bounds.x, {
                    type: "tween",
                    duration: 0.15,
                    ease: [0.25, 1, 0.5, 1],
                });
                animate(y, bounds.y, {
                    type: "tween",
                    duration: 0.15,
                    ease: [0.25, 1, 0.5, 1],
                });
            } else {
                if (isNotchedWheel) {
                    animate(x, dx, {
                        type: "tween",
                        duration,
                        ease: [0.25, 1, 0.5, 1],
                    });

                    animate(y, dy, {
                        type: "tween",
                        duration,
                        ease: [0.25, 1, 0.5, 1],
                    });
                } else {
                    x.set(dx);
                    y.set(dy);
                }
            }

            if (isNotchedWheel) {
                animate(scale, dScale, {
                    type: "tween",
                    duration,
                    ease: [0.25, 1, 0.5, 1],
                });
            } else {
                scale.set(dScale);
            }
            return memo;
        },
    }, {
        target: interactiveRef,
        enabled,
        eventOptions: { passive: false },
        pinch: {
            scaleBounds: {
                min: minScale,
                max: maxScale
            },
            rubberband: false,
            from: () => [scale.get(), 0],
            pinchOnWheel: false,
        },
        wheel: {
            from: () => [0, -scale.get() * RatioWheel],
            axis: "y",
            bounds: () => {
                return {
                    top: -maxScale * RatioWheel,
                    bottom: -minScale * RatioWheel
                }
            }
        },
        drag: {
            from: () => [x.get(), y.get()],
            rubberband: true,
            bounds: getBounds,
            filterTaps: true,
            tapsThreshold: 6 // default is 3px
        }
    });

    const handleReset = () => {
        gameSound.play(MAPA_SOUNDS.MOVE);
        if (scale.get() === 1) {
            scale.set(.98)
            animate(scale, 1, {
                type: "spring",
                stiffness: 100,
                duration: 0.2,
            });
        } else {
            animate(scale, 1, {
                type: "tween",
                duration: 0.2,
                ease: [0.25, 1, 0.5, 1],
            });
        }

        centerMap();
    }

    const handleZoom = async (value) => {
        if (zooming) return;

        setZooming(true);
        const zoom = scale.get();
        const newZoom = zoom + value;
        if (newZoom <= minScale) {
            if (zoom === 1) {
                scale.set(.98)
            }

            const animations = [
                animate(scale, minScale, {
                    type: "spring",
                    stiffness: 100,
                    duration: 0.2,
                }),
                centerMap()
            ];

            await Promise.all(animations);

            setZooming(false);
            return;
        }

        if (newZoom >= maxScale) {
            if (zoom === maxScale) {
                scale.set(maxScale + .02)
            }

            await animate(scale, maxScale, {
                type: "spring",
                stiffness: 100,
                duration: 0.2,
            })

            setZooming(false);
            return;
        }

        await animate(scale, newZoom, {
            type: "tween",
            duration: 0.4,
            ease: [0.25, 1, 0.5, 1],
        });

        const point = calculateAdjustedBounds({
            x: x.get(),
            y: y.get(),
        });

        x.set(point.x);
        y.set(point.y);

        setZooming(false);
    }

    const styleImage = animated ? { opacity: 0, y: '-100%' } : { opacity: 1, y: 0 }
    const styleNavButton = animated
        ? { opacity: 0, y: isDesktop ? 0 : "100%", x: isDesktop ? "100%" : 0 }
        : { opacity: 1, y: 0 }


    return (
        <div ref={containerRef} className={clsx(classes.wrapper)}>
            {loading && <Loading/>}
            <motion.div style={{ x, y, scale, height: bounds.height, width: bounds.width }} className={classes.content}>
                <motion.img
                    style={styleImage}
                    data-animate-id="image"
                    className={classes.image}
                    src={src}
                    ref={imageRef}
                    alt=""
                />

                {formattedPoints.map((point) => (
                    <motion.div
                        id={point.id}
                        transformTemplate={({ scale, x, y }) => {
                            return `translate(${x} , ${y}) scale(${1 / scale})`;
                        }}
                        key={point.id}
                        className={classes.point}
                        data-animate-id="point"
                        data-active={pointHoverId === point.id && !isTouchDevice}
                        style={{ x: point.x, y: point.y, scale, opacity: animated ? 0 : 1 }}
                    >
                        {point.component}
                    </motion.div>
                ))}
                <div ref={interactiveRef} className={classes.interactiveArea} style={{ cursor }} />
            </motion.div>

            <footer className={clsx(isTouchDevice && classes.footerdown, classes.footer)}>
                {!isTouchDevice &&
                    (
                        <>
                            <motion.button style={styleNavButton} data-animate-id="nav-button" type="button" className={clsx(isTouchDevice && classes.dontShow, classes.zoomInButton)} onClick={() => handleZoom(.75)}>
                                <ZoomInIcon />
                            </motion.button>
                            <motion.button style={styleNavButton} data-animate-id="nav-button" type="button" className={clsx(isTouchDevice && classes.dontShow, classes.zoomOutButton)} onClick={() => handleZoom(-.75)}>
                                <ZoomOutIcon />
                            </motion.button>
                        </>
                    )}
                <motion.button style={styleNavButton} data-animate-id="nav-button" type="button" className={clsx(scale.current > 1 && classes.centerButtonactived, classes.centerButton)} onClick={handleReset}>
                    <CenterIcon/>
                </motion.button>
            </footer>
        </div>

    )
})

export default InteractiveMap;

InteractiveMap.propTypes = {
    src: PropTypes.string.isRequired,
    points: PropTypes.arrayOf(
        PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
        })
    ).isRequired,
    animated: PropTypes.bool,
};